import {
  addMonths,
  monthsBetween,
  parseYearMonth,
  yearMonth,
  type MonthIndex,
  type YearMonth,
} from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { formatNumber, monthNames } from '../lib/format';
import { useLocale } from '../lib/i18n/use-locale';
import { parseNumber } from '../lib/parse';
import { CommitField } from './commit-field';
import { Select } from './select';

/*
 * Typed, locale-aware inputs built on CommitField. All commit on Enter/blur and revert on Escape.
 */

interface BaseProps {
  id?: string | undefined;
  'aria-label'?: string;
  'aria-describedby'?: string | undefined;
  className?: string;
}

type Parse<T> = (text: string) => { ok: true; value: T } | { ok: false; error: string };

function useNumberParse(min: number, max: number): Parse<number> {
  const { t } = useTranslation();
  const locale = useLocale();
  return (text) => {
    const value = parseNumber(text, locale);
    if (value === null) return { ok: false, error: t('fields.invalidNumber') };
    if (value < min || value > max) {
      return {
        ok: false,
        error: t('fields.outOfRange', {
          min: formatNumber(min, locale, 2),
          max: formatNumber(max, locale, 2),
        }),
      };
    }
    return { ok: true, value };
  };
}

export interface MoneyFieldProps extends BaseProps {
  value: number;
  onCommit: (value: number) => void;
  symbol: string;
  min?: number;
  max?: number;
}

export function MoneyField({
  value,
  onCommit,
  symbol,
  min = 0,
  max = 1e12,
  ...rest
}: MoneyFieldProps) {
  const locale = useLocale();
  const parse = useNumberParse(min, max);
  return (
    <CommitField
      {...rest}
      numeric
      value={value}
      format={(v) => formatNumber(v, locale, 2)}
      parse={parse}
      onCommit={onCommit}
      adornment={symbol}
    />
  );
}

export interface OptionalMoneyFieldProps extends BaseProps {
  value: number | undefined;
  onCommit: (value: number | undefined) => void;
  symbol: string;
  placeholder: string;
}

/** Money field where an empty input means "not set". */
export function OptionalMoneyField({
  value,
  onCommit,
  symbol,
  placeholder,
  ...rest
}: OptionalMoneyFieldProps) {
  const locale = useLocale();
  const parse = useNumberParse(0, 1e12);
  return (
    <CommitField<number | undefined>
      {...rest}
      numeric
      value={value}
      placeholder={placeholder}
      format={(v) => (v === undefined ? '' : formatNumber(v, locale, 2))}
      parse={(text) => (text.trim() === '' ? { ok: true, value: undefined } : parse(text))}
      onCommit={onCommit}
      adornment={symbol}
    />
  );
}

export interface PercentFieldProps extends BaseProps {
  /** Fraction: 0.031 is shown as 3.1 %. */
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
}

export function PercentField({
  value,
  onCommit,
  min = -99,
  max = 1000,
  ...rest
}: PercentFieldProps) {
  const locale = useLocale();
  const parse = useNumberParse(min, max);
  return (
    <CommitField
      {...rest}
      numeric
      value={value}
      format={(v) => formatNumber(Math.round(v * 1e8) / 1e6, locale, 3)}
      parse={(text) => {
        const r = parse(text.replace('%', ''));
        return r.ok ? { ok: true, value: r.value / 100 } : r;
      }}
      onCommit={onCommit}
      adornment="%"
    />
  );
}

export interface DecimalFieldProps extends BaseProps {
  value: number;
  onCommit: (value: number) => void;
  min: number;
  max: number;
  fractionDigits?: number;
  adornment?: string;
}

export function DecimalField({
  value,
  onCommit,
  min,
  max,
  fractionDigits = 2,
  adornment,
  ...rest
}: DecimalFieldProps) {
  const locale = useLocale();
  const parse = useNumberParse(min, max);
  return (
    <CommitField
      {...rest}
      numeric
      value={value}
      format={(v) => formatNumber(v, locale, fractionDigits)}
      parse={parse}
      onCommit={onCommit}
      {...(adornment === undefined ? {} : { adornment })}
    />
  );
}

export interface IntegerFieldProps extends BaseProps {
  value: number;
  onCommit: (value: number) => void;
  min: number;
  max: number;
  adornment?: string;
}

export function IntegerField({ value, onCommit, min, max, adornment, ...rest }: IntegerFieldProps) {
  const parse = useNumberParse(min, max);
  return (
    <CommitField
      {...rest}
      numeric
      value={value}
      format={(v) => String(v)}
      parse={(text) => {
        const r = parse(text);
        return r.ok ? { ok: true, value: Math.round(r.value) } : r;
      }}
      onCommit={onCommit}
      {...(adornment === undefined ? {} : { adornment })}
    />
  );
}

export interface TextFieldProps extends BaseProps {
  value: string;
  onCommit: (value: string) => void;
  maxLength?: number;
}

export function TextField({ value, onCommit, maxLength = 120, ...rest }: TextFieldProps) {
  const { t } = useTranslation();
  return (
    <CommitField
      {...rest}
      value={value}
      format={(v) => v}
      parse={(text) => {
        const trimmed = text.trim();
        if (trimmed === '') return { ok: false, error: t('fields.required') };
        return { ok: true, value: trimmed.slice(0, maxLength) };
      }}
      onCommit={onCommit}
    />
  );
}

export interface MonthFieldProps extends BaseProps {
  value: MonthIndex;
  onCommit: (value: MonthIndex) => void;
  /** Calendar month of month index 0. */
  start: YearMonth;
  /** Highest allowed month index. */
  max: number;
  min?: number;
}

/** Month + year picker editing a month index relative to the project start. */
export function MonthField({
  value,
  onCommit,
  start,
  max,
  min = 0,
  id,
  className,
  ...aria
}: MonthFieldProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { year, month } = parseYearMonth(addMonths(start, value));
  const names = monthNames(locale, 'long');
  const first = parseYearMonth(addMonths(start, min));
  const last = parseYearMonth(addMonths(start, max));

  const commit = (y: number, m: number): void => {
    const index = Math.min(max, Math.max(min, monthsBetween(start, yearMonth(y, m))));
    if (index !== value) onCommit(index as MonthIndex);
  };

  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_4rem] gap-1.5 ${className ?? ''}`}>
      <Select
        {...(id === undefined ? {} : { id })}
        aria-label={
          aria['aria-label'] ? `${aria['aria-label']} — ${t('fields.month')}` : t('fields.month')
        }
        value={String(month)}
        options={names.map((name, i) => ({ value: String(i + 1), label: name }))}
        onValueChange={(m) => {
          commit(year, Number(m));
        }}
      />
      <IntegerField
        aria-label={
          aria['aria-label'] ? `${aria['aria-label']} — ${t('fields.year')}` : t('fields.year')
        }
        value={year}
        min={first.year}
        max={last.year}
        onCommit={(y) => {
          commit(y, month);
        }}
      />
    </div>
  );
}
